/**
 * React NodeView for the customPanel node. Reads `color` + `icon`
 * attributes from the node and resolves them against the catalogs in
 * `panel-kinds.ts`.
 *
 * The color + icon picker lives INSIDE this NodeView as a footer band
 * (see `<CustomPanelPicker>`). Rationale: prior tippy-based
 * `CustomPanelBubbleMenu` (deleted 2026-05-29) re-positioned every
 * transaction as the panel grew with typed content, visually reading
 * as "the picker is moving while I type." The inline footer keeps
 * the picker visually fixed at the panel's bottom edge as a real
 * layout child — no floating popover, no re-anchoring.
 *
 * Visibility: the footer renders only when this specific panel
 * contains the editor selection (see `CustomPanelPicker.isActive`).
 */

import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { useTranslation } from 'react-i18next'

import { PanelPicker } from './PanelPicker'
import {
  type CustomPanelColor,
  type CustomPanelIcon,
  DEFAULT_CUSTOM_PANEL_COLOR,
  DEFAULT_CUSTOM_PANEL_ICON,
  isCustomPanelColor,
  isCustomPanelIcon,
  resolveCustomPanelColorClasses,
  resolveCustomPanelIcon,
} from './panel-kinds'

export function CustomPanelView({ node, editor, getPos }: ReactNodeViewProps<HTMLElement>) {
  const { t } = useTranslation('wiki')
  const rawColor = node.attrs.color as string | undefined
  const rawIcon = node.attrs.icon as string | undefined
  const colorSlug: CustomPanelColor = isCustomPanelColor(rawColor)
    ? rawColor
    : DEFAULT_CUSTOM_PANEL_COLOR
  const iconSlug: CustomPanelIcon = isCustomPanelIcon(rawIcon) ? rawIcon : DEFAULT_CUSTOM_PANEL_ICON
  const { panelBgClass, iconDiskClass } = resolveCustomPanelColorClasses(colorSlug)
  const Icon = resolveCustomPanelIcon(iconSlug)

  const ariaLabel = t('panel.custom.ariaLabel', { color: colorSlug })
  const forcedLabel = t('panel.custom.label')

  return (
    <NodeViewWrapper
      as="div"
      data-block-type="custom-panel"
      data-color={colorSlug}
      data-icon={iconSlug}
      // biome-ignore lint/a11y/useSemanticElements: NodeViewWrapper renders a div with ProseMirror state attached; a real <section> would change PM's content model around this block. role="region" + aria-label gives the right ARIA shape.
      role="region"
      aria-label={ariaLabel}
      // Outer wrapper: `relative` so the floating picker below can
      // anchor against it. No tinted bg or rounded corners here —
      // those live on the inner chrome div so the picker's float
      // doesn't extend the panel's tint.
      className="group/panel relative my-4"
    >
      <span className="forced-color-label sr-only" aria-hidden="true">
        {forcedLabel}
      </span>

      {/* Drag handle removed 2026-05-29 — unreachable at `-start-6`. */}

      {/* Inner chrome — owns the tinted background, rounded corners,
          padding, and the actual flex row with icon + body. Keeps
          the tint scope tight so the picker below floats OUTSIDE
          the panel's visual surface. */}
      <div className={`rounded-lg p-4 ${panelBgClass}`}>
        {/* Alignment matches Confluence — see the rationale comment in
         * `NamedPanelView.tsx`. Body uses default `prose` (16px) with
         * `leading-6` (24px line) so the first-line line-box equals
         * the disk height; equal-height containers center naturally
         * with `items-start` (no `mt-[1px]` nudge, no first-paragraph
         * line-box pin). */}
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full ${iconDiskClass}`}
          >
            <Icon size={14} strokeWidth={2.5} className="text-white" aria-hidden="true" />
          </span>
          <NodeViewContent className="prose dark:prose-invert max-w-none flex-1 text-fg leading-6 prose-p:m-0 prose-p:leading-6" />
        </div>
      </div>

      {/* Floating color + icon + kebab picker — shared with the named
          panels. Reads attrs via props, writes back via the editor
          chain. Kebab carries the Delete action. */}
      <PanelPicker
        editor={editor}
        getPos={getPos ?? null}
        nodeName="customPanel"
        nodeSize={node.nodeSize}
        currentColor={colorSlug}
        currentIcon={iconSlug}
      />
    </NodeViewWrapper>
  )
}
